import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, ButtonBase, Skeleton, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight, ExpandMore, FileDownloadOutlined } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import { Button, FinancieraLogo, OfertaCard, PageHeader, SegmentedFilter } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { setSelectedOffer } from "@store/slices/marketPlaceSlice";
import { useLazyGetOffersQuery, usePatchOferSelectedMutation } from "@api/marketPlaceApis";
import { usePostValidateNextStageMutation } from "@api/applications";
import { usePostDocumentGenarateMutation } from "@api/documents";
import { LoanApplicationOfferResponse, PayloadRequestPatchOffer } from "@interfaces/MarketPlace";
import { ApiError } from "@interfaces/response";
import { PATHS_TOFU_V2, STATUS_CATALOG } from "@helpers/constantes";
import useStatusConfig from "@hooks/useStatusConfig";
import { DialogConfirmarOferta } from "@pages/producto/credito_simple/tofu_v2/DialogConfirmarOferta";
import { DialogDescartarOferta } from "@pages/producto/credito_simple/tofu_v2/DialogDescartarOferta";
import { COLUMNAS, toVista } from "@helpers/ofertasHelpers";
import { ClaveComparativa, ClaveOrden, OfertaVista } from "@interfaces/ofertas";

const SCREEN_STATUS = STATUS_CATALOG.oferta.code;

/** Las preofertas con garantía no se aceptan aquí: primero se captura el bien en garantía. */
const PATH_GARANTIAS = PATHS_TOFU_V2.informacionMultigarantias.path;

/** Tonos del diseño (`S_PreAprobacionMulti`) sin equivalente en la paleta del theme. */
const MUTED = "#7E90A4";
const TABLE_BORDER = "#D4E0EC";
const TABLE_RULE = "#E3EBF3";
const TABLE_HEAD_BG = "#DCE6F0";
const TABLE_HEAD_TEXT = "#04203A";
const TABLE_HEAD_ACTIVE_BG = "#FFF1DE";
const ROW_ALT_BG = "#F7FBFE";
const PRODUCTO_TAG = "#2E6FA3";
const WINNER_TEXT = "#E5820A";
const DISCARD = "#8CA0B4";
const NOTA_TEXT = "#5C6B7C";

/** Tarjetas por página del carrusel. */
const POR_PAGINA = 3;

const ORDEN_OPTIONS = [
  { value: "", label: "Relevancia" },
  { value: "monto", label: "Mayor monto" },
  { value: "tasa", label: "Menor tasa" },
  { value: "comision", label: "Menor comisión" },
];

const GARANTIA_OPTIONS = [
  { value: "todas", label: "Todas" },
  { value: "con", label: "Con" },
  { value: "sin", label: "Sin" },
];

interface CeldaComparativa {
  key: ClaveComparativa;
  value: string;
  /** `true` cuando esta oferta es la mejor de la columna. */
  win: boolean;
}

interface FilaComparativa {
  id: string;
  financiera: string;
  logo?: string;
  producto: string;
  conGarantia: boolean;
  celdas: CeldaComparativa[];
}

const ALTO_FILA = 66;

/** Flechas redondas del carrusel de tarjetas. */
const PAGER_BUTTON_SX = {
  width: 38,
  height: 38,
  borderRadius: "999px",
  border: "1px solid #E0E9F1",
  bgcolor: "background.paper",
  color: "primary.dark",
  boxShadow: "0 2px 8px rgba(6, 44, 78, 0.08)",
  "&.Mui-disabled": { opacity: 0.35 },
} as const;

/** Celda de encabezado: solo posición y fondo; el texto lo pone `HEAD_TEXT_SX`. */
const CELDA_HEAD_SX = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  height: 46,
  display: "flex",
  alignItems: "center",
  color: TABLE_HEAD_TEXT,
  bgcolor: TABLE_HEAD_BG,
  borderBottom: `2px solid ${TABLE_BORDER}`,
} as const;

/** Tipografía de los títulos de la tabla, compartida por las celdas fijas y las ordenables. */
const HEAD_TEXT_SX = {
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
} as const;

/** Vista "Comparar": una fila por preoferta y el mejor valor de cada columna en ámbar. */
const TablaComparativa = ({
  filas,
  sortKey,
  disabled,
  onSortChange,
  onElegir,
  onDescartar,
}: {
  filas: FilaComparativa[];
  sortKey: ClaveOrden;
  disabled: boolean;
  onSortChange: (key: ClaveOrden) => void;
  onElegir: (id: string) => void;
  onDescartar: (id: string) => void;
}) => (
  <Box sx={{ overflowX: "auto" }}>
    <Box
      sx={{
        minWidth: 900,
        border: `1px solid ${TABLE_BORDER}`,
        borderRadius: "16px",
        overflow: "hidden",
        bgcolor: "background.paper",
        boxShadow: "0 10px 30px rgba(6, 44, 78, 0.07)",
      }}
    >
      <Box sx={{ maxHeight: 470, overflowY: "auto" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `minmax(150px, 1.05fr) minmax(140px, 1fr) repeat(${COLUMNAS.length}, 1fr) 130px`,
          }}
        >
          <Typography sx={{ ...CELDA_HEAD_SX, ...HEAD_TEXT_SX, px: "20px" }}>Financiera</Typography>
          <Typography sx={{ ...CELDA_HEAD_SX, ...HEAD_TEXT_SX, px: "14px", borderLeft: `1px solid ${TABLE_RULE}` }}>
            Producto
          </Typography>
          {COLUMNAS.map((columna) => {
            const activa = sortKey === columna.key;
            return (
              <ButtonBase
                key={columna.key}
                onClick={() => onSortChange(activa ? "" : columna.key)}
                data-testid={`ofertas-tabla-orden-${columna.key}`}
                sx={{
                  ...CELDA_HEAD_SX,
                  justifyContent: "center",
                  gap: "5px",
                  px: "10px",
                  color: activa ? "primary.accent" : TABLE_HEAD_TEXT,
                  bgcolor: activa ? TABLE_HEAD_ACTIVE_BG : TABLE_HEAD_BG,
                  borderLeft: `1px solid ${TABLE_RULE}`,
                  transition: "background-color .15s, color .15s",
                }}
              >
                <Typography component="span" sx={{ ...HEAD_TEXT_SX, color: "inherit" }}>
                  {columna.label}
                </Typography>
                <ExpandMore sx={{ fontSize: 13, opacity: activa ? 1 : 0.4, flexShrink: 0 }} />
              </ButtonBase>
            );
          })}
          <Box sx={{ ...CELDA_HEAD_SX, borderLeft: `1px solid ${TABLE_RULE}` }} />

          {filas.map((fila, index) => {
            const bg = index % 2 === 0 ? "background.paper" : ROW_ALT_BG;
            return (
              <Fragment key={fila.id}>
                <Box
                  sx={{
                    height: ALTO_FILA,
                    px: "20px",
                    display: "flex",
                    alignItems: "center",
                    bgcolor: bg,
                    borderBottom: `1px solid ${TABLE_RULE}`,
                  }}
                >
                  <FinancieraLogo logo={fila.logo} financiera={fila.financiera} maxHeight={30} maxWidth={110} />
                </Box>

                <Box
                  sx={{
                    height: ALTO_FILA,
                    px: "14px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: "2px",
                    bgcolor: bg,
                    borderBottom: `1px solid ${TABLE_RULE}`,
                    borderLeft: `1px solid ${TABLE_RULE}`,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: 12.5, color: "primary.dark" }}>
                    {fila.producto}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: 10.5,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      color: PRODUCTO_TAG,
                    }}
                  >
                    {fila.conGarantia ? "Con garantía" : "Sin garantía"}
                  </Typography>
                </Box>

                {fila.celdas.map((celda) => (
                  <Typography
                    key={celda.key}
                    sx={{
                      height: ALTO_FILA,
                      px: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      fontWeight: celda.win ? 700 : 600,
                      fontSize: 13.5,
                      color: celda.win ? WINNER_TEXT : "primary.dark",
                      bgcolor: bg,
                      borderBottom: `1px solid ${TABLE_RULE}`,
                      borderLeft: `1px solid ${TABLE_RULE}`,
                    }}
                  >
                    {celda.value}
                  </Typography>
                ))}

                <Box
                  sx={{
                    height: ALTO_FILA,
                    px: "14px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: "4px",
                    bgcolor: bg,
                    borderBottom: `1px solid ${TABLE_RULE}`,
                    borderLeft: `1px solid ${TABLE_RULE}`,
                  }}
                >
                  <Button
                    tone="accent"
                    fullWidth
                    disabled={disabled}
                    onClick={() => onElegir(fila.id)}
                    sx={{ px: 0, py: "9px", fontSize: 12.5, borderRadius: "8px" }}
                    data-testid={`ofertas-tabla-${fila.id}-elegir-button`}
                  >
                    Elegir
                  </Button>
                  <ButtonBase
                    onClick={() => onDescartar(fila.id)}
                    disabled={disabled}
                    data-testid={`ofertas-tabla-${fila.id}-descartar-button`}
                    sx={{
                      p: "2px",
                      fontWeight: 600,
                      fontSize: 11,
                      color: DISCARD,
                      textDecoration: "underline",
                      textUnderlineOffset: 3,
                      "&:hover": { color: NOTA_TEXT },
                      "&.Mui-disabled": { opacity: 0.5 },
                    }}
                  >
                    No me interesa
                  </ButtonBase>
                </Box>
              </Fragment>
            );
          })}
        </Box>
      </Box>
    </Box>

    <Typography sx={{ display: "flex", alignItems: "center", gap: "7px", mt: "12px", fontSize: 12, color: MUTED }}>
      <Box component="span" sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: "primary.accent" }} />
      En naranja, la mejor opción en cada categoría.
    </Typography>
  </Box>
);

export const Ofertas = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const company = useSelector((state: RootState) => state.register.company);
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);

  const [getOffersMarket] = useLazyGetOffersQuery();
  const [patchOfferSelected] = usePatchOferSelectedMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [triggerPostDocumentGenerate] = usePostDocumentGenarateMutation();

  const [offersList, setOffersList] = useState<LoanApplicationOfferResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [vista, setVista] = useState("tarjetas");
  const [sortKey, setSortKey] = useState<ClaveOrden>("");
  const [filtroGarantia, setFiltroGarantia] = useState("todas");
  const [page, setPage] = useState(0);
  const [descartadas, setDescartadas] = useState(0);
  const [porElegir, setPorElegir] = useState<OfertaVista | null>(null);
  const [porDescartar, setPorDescartar] = useState<OfertaVista | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const responseOffers = await getOffersMarket({ id_sol: application.id }).unwrap();
        setOffersList(responseOffers.filter((offer) => offer.active));
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "Ofertas-initial_fetch" } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const vistas = useMemo(() => offersList.map(toVista), [offersList]);

  const visibles = useMemo(() => {
    const filtradas = vistas.filter(
      (vistaOferta) =>
        filtroGarantia === "todas" ||
        (filtroGarantia === "sin" ? !vistaOferta.orden.conGarantia : vistaOferta.orden.conGarantia),
    );

    switch (sortKey) {
      case "monto":
        return [...filtradas].sort((a, b) => b.orden.monto - a.orden.monto);
      case "tasa":
        return [...filtradas].sort((a, b) => a.orden.tasa - b.orden.tasa);
      case "tiempo":
        return [...filtradas].sort((a, b) => a.orden.tiempo - b.orden.tiempo);
      case "garantia":
        // Sin garantía primero.
        return [...filtradas].sort((a, b) => Number(a.orden.conGarantia) - Number(b.orden.conGarantia));
      case "comision":
        return [...filtradas].sort((a, b) => a.orden.comision - b.orden.comision);
      case "plazo":
        return [...filtradas].sort((a, b) => b.orden.plazo - a.orden.plazo);
      default:
        return filtradas;
    }
  }, [vistas, sortKey, filtroGarantia]);

  // Cualquier cambio en el listado puede dejar la página actual vacía.
  useEffect(() => {
    setPage(0);
  }, [sortKey, filtroGarantia, vista, offersList]);

  /** Mejor valor de cada columna entre las ofertas visibles: es lo que la tabla marca en ámbar. */
  const mejores = useMemo(
    () => ({
      monto: Math.max(...visibles.map((item) => item.orden.monto)),
      tasa: Math.min(...visibles.map((item) => item.orden.tasa)),
      tiempo: Math.min(...visibles.map((item) => item.orden.tiempo)),
      comision: Math.min(...visibles.map((item) => item.orden.comision)),
      plazo: Math.max(...visibles.map((item) => item.orden.plazo)),
    }),
    [visibles],
  );

  const esMejor = (item: OfertaVista, key: ClaveComparativa) => {
    switch (key) {
      case "monto":
        return item.orden.monto === mejores.monto;
      case "tasa":
        return item.orden.tasa === mejores.tasa;
      case "tiempo":
        return item.orden.tiempo === mejores.tiempo;
      case "comision":
        return item.orden.comision === mejores.comision;
      case "plazo":
        return item.orden.plazo === mejores.plazo;
      // No hay "mejor" garantía numérica: gana no pedir garantía.
      case "garantia":
        return !item.orden.conGarantia;
    }
  };

  const filas: FilaComparativa[] = visibles.map((item) => ({
    id: item.card.id,
    financiera: item.card.financiera,
    logo: item.card.logo,
    producto: item.card.producto,
    conGarantia: item.card.conGarantia,
    celdas: COLUMNAS.map((columna) => ({
      key: columna.key,
      value: item.lista[columna.key],
      win: esMejor(item, columna.key),
    })),
  }));

  const porId = (id: string) => visibles.find((item) => item.card.id === id) ?? null;

  const abrirConfirmacion = (item: OfertaVista | null) => {
    if (isLoading) return;
    setPorElegir(item);
  };

  const abrirDescarte = (item: OfertaVista | null) => {
    if (isLoading) return;
    setPorDescartar(item);
  };

  /** Acepta la preoferta y avanza el status de la solicitud. */
  const seleccionarOferta = async (offer: LoanApplicationOfferResponse) => {
    try {
      setIsLoading(true);

      // Las preofertas con garantía no se aceptan aquí: el PATCH de aceptación lo hace la
      // pantalla de garantías al terminar de capturar los bienes.
      // TODO: `InformacionMultiGarantias` (wizard nuevo) todavía no hace ese
      // `patchOferSelected({ accept: true })` que sí hace el legacy `InformacionGarantiasPymio`,
      // así que hoy esta rama deja la oferta sin aceptar en el backend.
      if (offer.details?.withGuarantee) {
        dispatch(setSelectedOffer(offer));
        navigate(PATH_GARANTIAS);
        return;
      }

      if (!nextStatus) {
        throw new Error("No se encontró el siguiente status");
      }

      const data: PayloadRequestPatchOffer = { accept: true };
      await patchOfferSelected({ id_offer: offer.id, id_sol: application.id, data }).unwrap();

      // El legacy elige el status a mano según `is_partner` (CAOAC vs COFAC); aquí se usa el
      // siguiente status que devuelve `status_config` para el avance real de la solicitud.
      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();

      dispatch(setSelectedOffer(offer));
      navigate(nextStatus.url || "");
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "Ofertas-seleccionarOferta", offerId: offer.id } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoading(false);
    }
  };

  /** Descarta la preoferta: sale del listado y no vuelve a aparecer. */
  const descartarOferta = async (offer: LoanApplicationOfferResponse) => {
    try {
      setIsLoading(true);

      const data: PayloadRequestPatchOffer = { accept: false, declined: true };
      await patchOfferSelected({ id_offer: offer.id, id_sol: application.id, data }).unwrap();

      setOffersList((previas) => previas.filter((item) => item.id !== offer.id));
      setDescartadas((total) => total + 1);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "Ofertas-descartarOferta", offerId: offer.id } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoading(false);
    }
  };

  /** Genera y descarga el PDF con la comparativa de ofertas. */
  const descargarComparativa = async () => {
    try {
      setIsLoading(true);
      const response = await triggerPostDocumentGenerate({
        loan_application_id: application.id,
        document_type: "tabla_ofertas",
      }).unwrap();

      const base64Data = typeof response === "string" ? response : String(response ?? "");
      if (!base64Data) {
        throw new Error("No se recibió data válida");
      }

      const cleanBase64 = base64Data.replace(/\s/g, "");
      const byteCharacters = atob(cleanBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i += 1) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const blob = new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `comparativa_ofertas_${new Date().toISOString().split("T")[0]}.pdf`;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();

      // TODO: el legacy además abre el PDF en una pestaña nueva (`window.open(url, "_blank")`)
      // 500 ms después, como respaldo de la descarga. Se omitió a propósito porque duplica la
      // entrega del documento y los bloqueadores de pop-ups lo cortan; reponerlo si en QA se
      // detecta algún navegador donde el `link.click()` no baste.
      //
      // Igual que el legacy: se limpia el link y la URL temporal un rato después de disparar
      // la descarga, para no cortarla en navegadores que la procesan en diferido.
      setTimeout(() => {
        if (document.body.contains(link)) document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 5000);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "Ofertas-descargarComparativa" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoading(false);
    }
  };

  const paginas = Math.ceil(visibles.length / POR_PAGINA);
  const sinOfertas = !isLoading && offersList.length === 0;

  return (
    <Box>
      <PageHeader
        eyebrow={company.name ? `PRE-APROBACIÓN · ${company.name}` : "PRE-APROBACIÓN"}
        title={
          <>
            ¡Tenemos buenas noticias! Tienes{" "}
            <Box component="span" sx={{ color: "primary.accent" }}>
              {offersList.length} preoferta{offersList.length === 1 ? "" : "s"}
            </Box>
          </>
        }
        subtitle={
          <>
            Revisa los detalles, compara y elige la opción que mejor se adapte a tu negocio.
            <br />
            Las financieras pueden seguir ofertando e irán apareciendo nuevas ofertas aquí.
            <br />
            Recuerda: te recomendamos esperar al menos <strong>2 días</strong> para darles tiempo a todas, y solo puedes
            elegir <strong>una oferta</strong>.
          </>
        }
        fullWidthSubtitle
      />

      {!sinOfertas && (
        <>
          <Box sx={{ display: "flex", justifyContent: "center", m: "28px 0 22px" }}>
            <SegmentedFilter
              variant="view"
              value={vista}
              onChange={setVista}
              options={[
                { value: "tarjetas", label: "Vista Tarjetas" },
                { value: "listado", label: "Comparar" },
              ]}
              testIdPrefix="ofertas-vista"
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "nowrap",
              justifyContent: "center",
              alignItems: "center",
              gap: "14px",
              mb: "26px",
            }}
          >
            <SegmentedFilter
              label="Ordenar"
              value={sortKey}
              onChange={(value) => setSortKey(value as ClaveOrden)}
              options={ORDEN_OPTIONS}
              testIdPrefix="ofertas-orden"
            />
            <SegmentedFilter
              label="Garantía"
              value={filtroGarantia}
              onChange={setFiltroGarantia}
              options={GARANTIA_OPTIONS}
              testIdPrefix="ofertas-garantia"
            />
          </Box>
        </>
      )}

      {isLoading && offersList.length === 0 && (
        <Box sx={{ display: "flex", gap: "18px", justifyContent: "center" }}>
          {[0, 1, 2].map((index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              animation="wave"
              sx={{ flex: `0 0 calc((100% - 48px) / ${POR_PAGINA})`, height: 620, borderRadius: "20px" }}
            />
          ))}
        </Box>
      )}

      {sinOfertas && (
        <Typography sx={{ textAlign: "center", p: "48px 20px", fontSize: 17, fontWeight: 600, color: "primary.dark" }}>
          No cuenta con ofertas disponibles en este momento.
        </Typography>
      )}

      {offersList.length > 0 && visibles.length === 0 && (
        <Typography sx={{ textAlign: "center", p: "48px 20px", fontSize: 15, color: MUTED }}>
          No hay ofertas que coincidan con estos filtros.
        </Typography>
      )}

      {visibles.length > 0 && vista === "listado" && (
        <TablaComparativa
          filas={filas}
          sortKey={sortKey}
          disabled={isLoading}
          onSortChange={setSortKey}
          onElegir={(id) => abrirConfirmacion(porId(id))}
          onDescartar={(id) => abrirDescarte(porId(id))}
        />
      )}

      {/* Hasta tres preofertas caben en una fila; de ahí en adelante el diseño pagina de tres en tres. */}
      {visibles.length > 0 && visibles.length <= POR_PAGINA && vista === "tarjetas" && (
        <Box sx={{ display: "flex", gap: "18px", justifyContent: "center", p: "18px 22px 32px", m: "-18px -22px 0" }}>
          {visibles.map((item) => (
            <Box
              key={item.card.id}
              sx={{ flex: `0 0 calc((100% - 48px) / ${POR_PAGINA})`, minWidth: 260, maxWidth: 380 }}
            >
              <OfertaCard
                oferta={item.card}
                disabled={isLoading}
                onElegir={() => abrirConfirmacion(item)}
                onDescartar={() => abrirDescarte(item)}
              />
            </Box>
          ))}
        </Box>
      )}

      {visibles.length > POR_PAGINA && vista === "tarjetas" && (
        <>
          <Box sx={{ overflowX: "clip", overflowY: "visible", p: "18px 0 32px", m: "-18px 0 0" }}>
            <Box
              sx={{
                display: "flex",
                gap: "18px",
                transform: `translateX(calc(-1 * ${page} * (100% - 26px)))`,
                transition: "transform .42s cubic-bezier(.4,0,.2,1)",
              }}
            >
              {visibles.map((item, index) => {
                // Las tarjetas de las otras páginas se ven de fondo, desenfocadas.
                const fueraDePagina = Math.floor(index / POR_PAGINA) !== page;
                return (
                  <Box
                    key={item.card.id}
                    sx={{
                      flex: `0 0 calc((100% - 80px) / ${POR_PAGINA})`,
                      filter: fueraDePagina ? "blur(3px)" : "none",
                      opacity: fueraDePagina ? 0.5 : 1,
                      transition: "filter .38s ease, opacity .38s ease",
                      pointerEvents: fueraDePagina ? "none" : "auto",
                    }}
                  >
                    <OfertaCard
                      oferta={item.card}
                      disabled={isLoading}
                      onElegir={() => abrirConfirmacion(item)}
                      onDescartar={() => abrirDescarte(item)}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "14px", mt: "20px" }}>
            <ButtonBase
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page <= 0}
              aria-label="Página anterior"
              data-testid="ofertas-pagina-anterior-button"
              sx={PAGER_BUTTON_SX}
            >
              <ChevronLeft sx={{ fontSize: 20 }} />
            </ButtonBase>

            <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {Array.from({ length: paginas }).map((_, index) => (
                <ButtonBase
                  key={index}
                  onClick={() => setPage(index)}
                  aria-label={`Ir a la página ${index + 1}`}
                  data-testid={`ofertas-pagina-${index + 1}-button`}
                  sx={{
                    width: page === index ? 22 : 9,
                    height: 9,
                    borderRadius: "999px",
                    bgcolor: page === index ? "primary.accent" : "#C9D6DE",
                    transition: "all .25s ease",
                  }}
                />
              ))}
            </Box>

            <ButtonBase
              onClick={() => setPage(Math.min(paginas - 1, page + 1))}
              disabled={page >= paginas - 1}
              aria-label="Página siguiente"
              data-testid="ofertas-pagina-siguiente-button"
              sx={PAGER_BUTTON_SX}
            >
              <ChevronRight sx={{ fontSize: 20 }} />
            </ButtonBase>
          </Box>
        </>
      )}

      {visibles.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: "24px" }}>
          <Button
            tone="secondary"
            disabled={isLoading}
            onClick={descargarComparativa}
            startIcon={<FileDownloadOutlined sx={{ fontSize: 15 }} />}
            data-testid="ofertas-descargar-comparativa-button"
          >
            Descargar comparativa de ofertas
          </Button>
        </Box>
      )}

      {descartadas > 0 && (
        <Typography sx={{ textAlign: "center", mt: "14px", fontSize: 12.5, color: MUTED }}>
          {descartadas} preoferta{descartadas === 1 ? "" : "s"} descartada{descartadas === 1 ? "" : "s"}
        </Typography>
      )}

      {porDescartar && (
        <DialogDescartarOferta
          oferta={porDescartar.card}
          disabled={isLoading}
          onConfirm={() => {
            const item = porDescartar;
            setPorDescartar(null);
            descartarOferta(item.offer);
          }}
          onClose={() => setPorDescartar(null)}
        />
      )}

      {porElegir && (
        <DialogConfirmarOferta
          oferta={porElegir.card}
          disabled={isLoading}
          onConfirm={() => {
            const item = porElegir;
            setPorElegir(null);
            seleccionarOferta(item.offer);
          }}
          onClose={() => setPorElegir(null)}
        />
      )}
    </Box>
  );
};
